import Logger from "../../../../utils/Logger";
import ResultWebService from "./ResultWebService";
import ResultParser from "./ResultParser";
import * as MscoreConst from "../MscoreStepExecutor";
import EQmScoreStepDone from "../../../../../public-apis/onclick/EQmScoreStepDone";
import * as ConfigWebServicesConst from "../../../../configuration/service/ConfigWebService";

const MINIMUM_RETURNED_MEAN_OPINION_SCORE = 1.0;
const MINIMUM_MEAN_OPINION_SCORE = 1.0;
const MAXIMUM_RETURNED_MEAN_OPINION_SCORE = 4.4;
const MAXIMUM_MEAN_OPINION_SCORE = 5.0;
const DEFAULT_GET_RESULTS_TIMEOUT = 5000;

/**
 * Retrieve the result of the mScore test
 */
export default class ResultManager {

    /**
     * Default constructor
     * @param {UrlBuilder} urlBuilder builder to format URL
     */
    constructor(urlBuilder) {
        this.urlBuilder = urlBuilder;
        this.resultWebService = new ResultWebService();
        this.resultParser = new ResultParser();
        this.timeout = false;
    }

    /**
     * @param {string} url to get results
     * @param {Array.<EQmScoreStepProgress>} rawData
     * @param {string} The best mScore server url
     * @param {ConfigStepMscore} configuration server
     * @return {Promise}
     */
    getResult(url, rawData, mScoreServerUrl, configuration) {
        const self = this;
        self.timeout = false;
        let timeoutTimer = null;

        return new Promise((resolve) => {
            const randomUrl = url + '&random=' + Math.random();
            Logger.log("Get results from " + randomUrl);

            timeoutTimer = setTimeout(() => {
                self.timeout = true;
                Logger.log("Timeout on get resbaseUrlults");
                resolve(self.computeResult(configuration, rawData, mScoreServerUrl));
            }, (configuration && configuration.params && configuration.params.posttimeout ? (parseInt(configuration.params.posttimeout) * 1000):DEFAULT_GET_RESULTS_TIMEOUT));

            this.resultWebService.getResult(randomUrl, rawData).then((result) => {
                if (!self.timeout) {
                    const mScoreResult = this.resultParser.parse(result);
                    if (timeoutTimer) {
                        clearTimeout(timeoutTimer);
                    }
                    resolve(this.computeResult(configuration, rawData, mScoreServerUrl, mScoreResult));
                }
            }).catch((error) => {
                if (!self.timeout) {
                    Logger.log(error);
                    if (timeoutTimer) {
                        clearTimeout(timeoutTimer);
                    }
                    resolve(this.computeResult(configuration, rawData, mScoreServerUrl));
                }
            });
        });
    }

    /**
     * Straighten Mean opinion score to value between 1 and 5
     *
     * @param mos
     * @returns {*} Null if no mos or out of bounds, otherwise straighten mos
     */
    straightenMos(mos) {
        if (mos) {
            let mosFloat = parseFloat(mos);

            if (MINIMUM_RETURNED_MEAN_OPINION_SCORE <= mosFloat && mosFloat <= MAXIMUM_RETURNED_MEAN_OPINION_SCORE) {
                const returnedRange = MAXIMUM_RETURNED_MEAN_OPINION_SCORE - MINIMUM_RETURNED_MEAN_OPINION_SCORE;
                const range = MAXIMUM_MEAN_OPINION_SCORE - MINIMUM_MEAN_OPINION_SCORE;

                return Math.round((((mosFloat - MINIMUM_RETURNED_MEAN_OPINION_SCORE) * range / returnedRange) + MINIMUM_MEAN_OPINION_SCORE) * 100) / 100;
            }
        }

        return null;
    }

    /**
     * Compute the test result from mScore if available
     *
     * @param {ConfigStepMscore} configuration server
     * @param {Array.<EQmScoreStepProgress>} rawData direction the direction of the test (dl/ul)
     * @param {string} The best mScore server url
     * @param {MscoreResult} result the result from mScore if available
     * @return {EQmScoreStepDone} the result
     */
    computeResult(configuration, rawData, mScoreServerUrl, result = null) {
        const lastRaw = rawData[rawData.length - 1];
        let maxThroughput;
        let avgThroughput;
        let minLatency;
        let time;
        let volume;
        let extras = null;
        let baseUrl = this.urlBuilder.buildBaseUrl(mScoreServerUrl);
        let mos = null;
        let rootCauseAnalysis1 = null;
        // EWA-39 Add flag to indicate if failed to get results from mScore server
        let computeFailed = false;
        if (result) {
            maxThroughput = result.maxThroughput;
            avgThroughput = result.avgThroughput;
            minLatency = result.minLatency;
            time = result.sessionTime;
            volume = result.volume;
            extras = result.extras;
            rootCauseAnalysis1 = result.rootCauseAnalysis1;
            mos = this.straightenMos(result.mos);
        } else {
            computeFailed = true;
            maxThroughput = this.getMaxThroughput(rawData);
            avgThroughput = (lastRaw.totalBytes / 1000) / (lastRaw.sessionTime / 1000) * 8;
            minLatency = -1;
            time = lastRaw.sessionTime;
            volume = lastRaw.totalBytes;
        }
        
        let chartUrl = null;
        if (result) {
            chartUrl = configuration.portalUrl + ConfigWebServicesConst.WEBSERVICE_EXT + "/mScoreChart/" + ((result && result.extras && result.extras.TEST) ? result.extras.TEST : "shooter") + "/" + ((result && result.extras && result.extras.DA_SID) ? result.extras.DA_SID : "unknown") + "?server=" + encodeURIComponent(baseUrl);
        }
        return new EQmScoreStepDone(MscoreConst.STATUS_SUCCESS,
            lastRaw.direction,
            configuration.getService(),
            configuration.getModule(),
            computeFailed,
            volume,
            time,
            maxThroughput,
            avgThroughput,
            minLatency,
            extras,
            baseUrl,
            mos,
            rootCauseAnalysis1,
            chartUrl
        );
    }

    /**
     * Compute the max throughput from raw data
     *
     * @param {Array.<EQmScoreStepProgress>} rawData direction the direction of the test (dl/ul)
     */
    getMaxThroughput(rawData) {
        let maxThroughput = 0;
        rawData.forEach(function (data) {
            if (maxThroughput < data.instantThroughput()) {
                maxThroughput = data.instantThroughput();
            }
        });

        return maxThroughput;
    }
}



// WEBPACK FOOTER //
// ./js/core/script/executors/mscore/result/ResultManager.js